import React, { useState } from 'react';
import clsx from 'clsx';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/20/solid';

/**
 * The one picker pattern (#603, Part 2 §5): a chip that opens a searchable
 * popover — typeahead at the top, options with icons, a check on the
 * current value. Used by filters, the properties panel and the create
 * modal alike, so they all behave the same.
 *
 * Built from Headless UI's Popover + Combobox, which supply focus
 * management, arrow-key navigation, Escape-to-close and the ARIA roles.
 *
 * A picker the user may not change renders as a plain, non-interactive
 * chip — never as a live control that the backend would 403.
 */
export interface PickerOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  /** Shown but not choosable, like a disabled <option>. */
  disabled?: boolean;
}

type Common = {
  options: PickerOption[];
  placeholder?: string;
  /** Chip content. Receives the current selection for convenience. */
  trigger: React.ReactNode;
  /** Accessible name for the chip, e.g. "Status: In Progress". */
  ariaLabel: string;
  readOnly?: boolean;
  busy?: boolean;
  /** Not usable: the trigger is shown but does not open. */
  disabled?: boolean;
  className?: string;
  /**
   * Drop the chip look entirely and style the trigger with `className`
   * alone. Tailwind resolves conflicting classes by stylesheet order, not
   * class order, so a field-shaped trigger cannot override `chipClass`.
   */
  unstyled?: boolean;
  /** On the wrapper, e.g. `block w-full` for a full-width field. */
  wrapperClassName?: string;
  /** Extra classes for the popover, e.g. a minimum width. */
  panelClassName?: string;
};

type Single = Common & {
  multiple?: false;
  value: string | null;
  onChange: (value: string) => void;
};

type Multi = Common & {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
};

export const chipClass =
  'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] leading-5 ' +
  'text-fg-secondary ' +
  'hover:bg-fg/5 ' +
  'focus:outline-none data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-1 data-[focus]:outline-accent ' +
  'transition-colors';

const panelClass =
  'z-50 w-60 rounded-lg border border-line bg-raised p-1 shadow-xl ' +
  '[--anchor-gap:6px] ' +
  'transition data-[closed]:opacity-0 data-[closed]:scale-95';

export const PropertyPicker: React.FC<Single | Multi> = (props) => {
  const {
    options,
    placeholder,
    trigger,
    ariaLabel,
    readOnly,
    busy,
    disabled,
    className,
    unstyled,
    wrapperClassName,
    panelClassName,
  } = props;
  const [query, setQuery] = useState('');

  if (readOnly) {
    return (
      <span
        className={clsx(
          !unstyled && chipClass,
          'cursor-default hover:bg-transparent',
          className
        )}
        aria-label={ariaLabel}
      >
        {trigger}
      </span>
    );
  }

  const q = query.trim().toLowerCase();
  const visible = q
    ? options.filter((o) => o.label.toLowerCase().includes(q))
    : options;

  const isSelected = (v: string) =>
    props.multiple ? props.value.includes(v) : props.value === v;

  return (
    <Popover className={clsx('relative', wrapperClassName ?? 'inline-block')}>
      <PopoverButton
        className={clsx(!unstyled && chipClass, busy && 'opacity-60', className)}
        aria-label={ariaLabel}
        disabled={busy || disabled}
      >
        {trigger}
      </PopoverButton>
      <PopoverPanel
        focus
        anchor="bottom start"
        transition
        className={clsx(panelClass, panelClassName)}
      >
        {({ close }) => {
          const inner = (
            <>
              <ComboboxInput
                autoFocus
                className="w-full rounded-md border-0 bg-transparent px-2 py-1.5 text-[13px] text-fg placeholder:text-fg-muted focus:outline-none focus:ring-0"
                placeholder={placeholder ?? 'Filter…'}
                // The box is for typing a filter, never for echoing the
                // current value — without this a single-select Combobox
                // prefills it with the raw API value ("InProgress").
                displayValue={() => query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') close();
                }}
              />
              <div className="my-1 h-px bg-line" />
              <ComboboxOptions static className="max-h-64 overflow-y-auto">
                {visible.length === 0 && (
                  <div className="px-2 py-1.5 text-[13px] text-fg-muted">No matches</div>
                )}
                {visible.map((o) => (
                  <ComboboxOption
                    key={o.value}
                    value={o.value}
                    disabled={o.disabled}
                    className="group flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-fg-secondary data-[focus]:bg-fg/[0.06] data-[disabled]:opacity-50"
                  >
                    {o.icon}
                    <span className="flex-1 truncate">{o.label}</span>
                    {isSelected(o.value) && (
                      <CheckIcon className="h-4 w-4 text-fg-muted" />
                    )}
                  </ComboboxOption>
                ))}
              </ComboboxOptions>
            </>
          );
          return props.multiple ? (
            <Combobox
              multiple
              value={props.value}
              onChange={(v: string[]) => props.onChange(v)}
            >
              {inner}
            </Combobox>
          ) : (
            <Combobox
              value={props.value}
              onChange={(v: string | null) => {
                if (v) props.onChange(v);
                setQuery('');
                close();
              }}
            >
              {inner}
            </Combobox>
          );
        }}
      </PopoverPanel>
    </Popover>
  );
};

/**
 * Date variant: the chip opens a native date input. Native because it
 * brings a keyboard-accessible, locale-aware calendar for free; the value
 * contract is a 'YYYY-MM-DD' day (see violationMeta's calendar helpers).
 */
export const DatePicker: React.FC<{
  value: string; // 'YYYY-MM-DD' or ''
  onChange: (day: string) => void;
  trigger: React.ReactNode;
  ariaLabel: string;
  readOnly?: boolean;
  busy?: boolean;
  className?: string;
}> = ({ value, onChange, trigger, ariaLabel, readOnly, busy, className }) => {
  const [draft, setDraft] = useState(value);
  if (readOnly) {
    return (
      <span
        className={clsx(chipClass, 'cursor-default hover:bg-transparent', className)}
        aria-label={ariaLabel}
      >
        {trigger}
      </span>
    );
  }
  return (
    <Popover className="relative inline-block">
      <PopoverButton
        className={clsx(chipClass, busy && 'opacity-60', className)}
        aria-label={ariaLabel}
        disabled={busy}
        onClick={() => setDraft(value)}
      >
        {trigger}
      </PopoverButton>
      <PopoverPanel focus anchor="bottom start" transition className={clsx(panelClass, 'w-auto p-2')}>
        {({ close }) => (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft && draft !== value) onChange(draft);
              close();
            }}
          >
            <input
              type="date"
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="rounded-md border border-line bg-transparent px-2 py-1 text-[13px] text-fg [color-scheme:light] focus:outline-none focus:ring-2 focus:ring-accent dark:[color-scheme:dark]"
            />
            <button
              type="submit"
              className="rounded-md bg-sky-700 px-2.5 py-1 text-[13px] font-medium text-white hover:bg-sky-600 disabled:opacity-50"
              disabled={!draft}
            >
              Set
            </button>
          </form>
        )}
      </PopoverPanel>
    </Popover>
  );
};
